import React, { useEffect, useState } from "react";
import { Box, IconButton, Paper, Popper, Typography } from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { AnimatePresence, motion } from "framer-motion";

const TooltipPaper = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "arrowPlacement",
})(({ theme, color = "primary", arrowPlacement = "bottom" }) => ({
    maxWidth: 280,
    padding: theme.spacing(2),
    backgroundColor: theme.palette[color].main,
    color: "#fff",
    borderRadius: 12,
    boxShadow: `0 8px 24px ${alpha(theme.palette[color].main, 0.4)}`,
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      left: "50%",
      transform: "translateX(-50%)",
      width: 0,
      height: 0,
      borderLeft: "8px solid transparent",
      borderRight: "8px solid transparent",
      ...(arrowPlacement === "top"
        ? {
            top: -8,
            borderBottom: `8px solid ${theme.palette[color].main}`,
          }
        : {
            bottom: -8,
            borderTop: `8px solid ${theme.palette[color].main}`,
          }),
    },
  })
);

const TutorialTooltip = ({
  open,
  anchorEl,
  message,
  emoji = "👋",
  onClose,
  color = "primary",
  placement = "right-start",
  delay = 1500,
}) => {
  const [show, setShow] = useState(false);

  const arrowPlacement = placement?.startsWith("bottom") ? "top" : "bottom";

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => setShow(true), delay);
      return () => clearTimeout(timer);
    } else {
      setShow(false);
    }
  }, [open, delay]);

  const handleClose = () => {
    setShow(false);
    setTimeout(onClose, 300); // Wait for animation
  };

  return (
    <Popper
      open={show && !!anchorEl}
      anchorEl={anchorEl}
      placement={placement}
      modifiers={[
        {
          name: "offset",
          options: {
            offset: [0, 12],
          },
        },
      ]}
      sx={{ zIndex: 9999 }}
    >
      <AnimatePresence>
        {show && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: -10 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <TooltipPaper elevation={8} color={color} arrowPlacement={arrowPlacement}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <Typography variant="h4" sx={{ lineHeight: 1 }}>
                  {emoji}
                </Typography>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: "#ffffff" }}
                  >
                    {message}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={handleClose}
                  sx={{
                    color: "#fff",
                    padding: 0.5,
                    "&:hover": {
                      backgroundColor: alpha("#fff", 0.2),
                    },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </TooltipPaper>
          </motion.div>
        )}
      </AnimatePresence>
    </Popper>
  );
};

export default TutorialTooltip;
